/**
 * CommandPalette.tsx
 *
 * Global search/command palette for EMS application.
 * Triggered by Cmd+K / Ctrl+K keyboard shortcut.
 *
 * Features:
 * - Real-time search filtering across all navigation items
 * - Keyboard navigation (Up/Down arrows, Enter to select, Escape to close)
 * - Recent searches tracking
 * - Grouped results by category (Navigation, Modules, Settings, Actions)
 * - Highlighted matching text
 * - Integration with React Router for navigation
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Clock,
  LayoutDashboard,
  Activity,
  Users,
  Shield,
  Settings,
  Terminal,
  BarChart3,
  FileText,
  Puzzle,
  Webhook,
  Zap,
  Server,
  HardDrive,
  MonitorPlay,
  Cloud,
  Box,
  Network,
  UserCog,
  Monitor,
  FolderOpen,
  Router,
  Cpu,
  Container,
  HardDriveDownload,
  ShieldCheck,
  Bug,
  Package,
  RefreshCw,
  Archive,
  AppWindow,
  Globe,
  Key,
  Database,
  Layers,
  Plus,
  Link,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface SearchItem {
  id: string;
  type: 'navigation' | 'module' | 'settings' | 'action';
  category: string;
  title: string;
  description?: string;
  path?: string;
  action?: () => void;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  keywords?: string[];
}

interface SearchGroup {
  category: string;
  items: SearchItem[];
}

// ============================================================================
// Search Items Configuration
// ============================================================================

const SEARCH_ITEMS: SearchItem[] = [
  // -------------------------------------------------------------------------
  // OVERVIEW / NAVIGATION
  // -------------------------------------------------------------------------
  {
    id: 'dashboard',
    type: 'navigation',
    category: 'Overview',
    title: 'Dashboard',
    description: 'View system overview and statistics',
    path: '/',
    icon: LayoutDashboard,
    keywords: ['home', 'main', 'overview'],
  },
  {
    id: 'activity',
    type: 'navigation',
    category: 'Overview',
    title: 'Activity Feed',
    description: 'View recent system activity',
    path: '/activity',
    icon: Activity,
    keywords: ['logs', 'history', 'events'],
  },

  // -------------------------------------------------------------------------
  // ADMINISTRATION
  // -------------------------------------------------------------------------
  {
    id: 'users',
    type: 'settings',
    category: 'Administration',
    title: 'Users',
    description: 'Manage user accounts',
    path: '/users',
    icon: Users,
    keywords: ['accounts', 'people', 'team'],
  },
  {
    id: 'roles',
    type: 'settings',
    category: 'Administration',
    title: 'Roles & Permissions',
    description: 'Configure access control',
    path: '/roles',
    icon: Shield,
    keywords: ['permissions', 'access', 'security', 'rbac'],
  },
  {
    id: 'modules',
    type: 'settings',
    category: 'Administration',
    title: 'Modules',
    description: 'Enable and configure modules',
    path: '/config/modules',
    icon: Puzzle,
    keywords: ['plugins', 'extensions', 'features'],
  },
  {
    id: 'feature-flags',
    type: 'settings',
    category: 'Administration',
    title: 'Feature Flags',
    description: 'Toggle experimental features',
    path: '/config/features',
    icon: Zap,
    keywords: ['experiments', 'beta', 'flags'],
  },
  {
    id: 'license-manager',
    type: 'settings',
    category: 'Administration',
    title: 'License Manager',
    description: 'Manage EMS licenses',
    path: '/config/licenses',
    icon: Key,
    keywords: ['licenses', 'activation', 'subscription'],
  },
  {
    id: 'settings',
    type: 'settings',
    category: 'Administration',
    title: 'Settings',
    description: 'Configure system settings',
    path: '/config/settings',
    icon: Settings,
    keywords: ['configuration', 'preferences', 'options'],
  },

  // -------------------------------------------------------------------------
  // SYSTEM
  // -------------------------------------------------------------------------
  {
    id: 'system-health',
    type: 'navigation',
    category: 'System',
    title: 'System Health',
    description: 'Monitor system performance',
    path: '/system/health',
    icon: Activity,
    keywords: ['monitoring', 'status', 'health'],
  },
  {
    id: 'system-logs',
    type: 'navigation',
    category: 'System',
    title: 'System Logs',
    description: 'View system logs',
    path: '/system/logs',
    icon: Terminal,
    keywords: ['logs', 'console', 'debug'],
  },

  // -------------------------------------------------------------------------
  // ANALYTICS
  // -------------------------------------------------------------------------
  {
    id: 'analytics',
    type: 'navigation',
    category: 'Analytics',
    title: 'Analytics Dashboard',
    description: 'View analytics and insights',
    path: '/analytics',
    icon: BarChart3,
    keywords: ['metrics', 'stats', 'insights'],
  },
  {
    id: 'reports',
    type: 'navigation',
    category: 'Analytics',
    title: 'Reports',
    description: 'Generate and view reports',
    path: '/analytics/reports',
    icon: FileText,
    keywords: ['reporting', 'documents', 'export'],
  },

  // -------------------------------------------------------------------------
  // INTEGRATIONS
  // -------------------------------------------------------------------------
  {
    id: 'integrations',
    type: 'navigation',
    category: 'Integrations',
    title: 'Connections',
    description: 'Manage external integrations',
    path: '/integrations',
    icon: Webhook,
    keywords: ['webhooks', 'api', 'external'],
  },
  {
    id: 'api-keys',
    type: 'navigation',
    category: 'Integrations',
    title: 'API Keys',
    description: 'Manage API access keys',
    path: '/integrations/api',
    icon: Key,
    keywords: ['tokens', 'authentication', 'credentials'],
  },

  // -------------------------------------------------------------------------
  // COMPUTE MODULES
  // -------------------------------------------------------------------------
  {
    id: 'servers',
    type: 'module',
    category: 'Compute',
    title: 'Linux Servers',
    description: 'Manage Linux servers',
    path: '/servers',
    icon: Server,
    keywords: ['linux', 'unix', 'ssh'],
  },
  {
    id: 'hp-servers',
    type: 'module',
    category: 'Compute',
    title: 'HP Servers (iLO)',
    description: 'Manage HP servers via iLO',
    path: '/hp-servers',
    icon: Cpu,
    keywords: ['ilo', 'hp', 'hardware'],
  },
  {
    id: 'dell-servers',
    type: 'module',
    category: 'Compute',
    title: 'Dell Servers (iDRAC)',
    description: 'Manage Dell servers via iDRAC',
    path: '/dell-servers',
    icon: Cpu,
    keywords: ['idrac', 'dell', 'hardware'],
  },
  {
    id: 'vmware-esxi',
    type: 'module',
    category: 'Compute',
    title: 'VMware ESXi',
    description: 'Manage ESXi hosts',
    path: '/vmware-esxi',
    icon: Server,
    keywords: ['vmware', 'hypervisor', 'virtualization'],
  },
  {
    id: 'vmware-vcsa',
    type: 'module',
    category: 'Compute',
    title: 'vCenter (VCSA)',
    description: 'Manage vCenter server',
    path: '/vmware-vcsa',
    icon: Cloud,
    keywords: ['vmware', 'vcenter', 'vsphere'],
  },
  {
    id: 'nutanix-clusters',
    type: 'module',
    category: 'Compute',
    title: 'Nutanix Clusters',
    description: 'Manage Nutanix clusters',
    path: '/nutanix/clusters',
    icon: Layers,
    keywords: ['nutanix', 'hci', 'hyperconverged'],
  },
  {
    id: 'nutanix-vms',
    type: 'module',
    category: 'Compute',
    title: 'Nutanix VMs',
    description: 'Manage Nutanix virtual machines',
    path: '/nutanix/vms',
    icon: HardDrive,
    keywords: ['nutanix', 'vm', 'virtual'],
  },
  {
    id: 'proxmox-clusters',
    type: 'module',
    category: 'Compute',
    title: 'Proxmox Clusters',
    description: 'Manage Proxmox clusters',
    path: '/proxmox/clusters',
    icon: Cloud,
    keywords: ['proxmox', 'pve', 'virtualization'],
  },
  {
    id: 'proxmox-vms',
    type: 'module',
    category: 'Compute',
    title: 'Proxmox VMs',
    description: 'Manage Proxmox virtual machines',
    path: '/proxmox/vms',
    icon: HardDrive,
    keywords: ['proxmox', 'vm', 'virtual'],
  },

  // -------------------------------------------------------------------------
  // CONTAINER MODULES
  // -------------------------------------------------------------------------
  {
    id: 'kubernetes-clusters',
    type: 'module',
    category: 'Containers',
    title: 'Kubernetes Clusters',
    description: 'Manage K8s clusters',
    path: '/kubernetes/clusters',
    icon: Cloud,
    keywords: ['k8s', 'kubernetes', 'orchestration'],
  },
  {
    id: 'kubernetes-pods',
    type: 'module',
    category: 'Containers',
    title: 'Kubernetes Pods',
    description: 'View and manage pods',
    path: '/kubernetes/pods',
    icon: Box,
    keywords: ['k8s', 'pods', 'containers'],
  },
  {
    id: 'kubernetes-deployments',
    type: 'module',
    category: 'Containers',
    title: 'Kubernetes Deployments',
    description: 'Manage deployments',
    path: '/kubernetes/deployments',
    icon: Layers,
    keywords: ['k8s', 'deploy', 'workloads'],
  },
  {
    id: 'docker-containers',
    type: 'module',
    category: 'Containers',
    title: 'Docker Containers',
    description: 'Manage Docker containers',
    path: '/docker/containers',
    icon: Container,
    keywords: ['docker', 'containerization'],
  },
  {
    id: 'docker-all-containers',
    type: 'module',
    category: 'Containers',
    title: 'All Containers',
    description: 'All Docker containers across every host',
    path: '/docker/all-containers',
    icon: Container,
    keywords: ['docker', 'containers', 'fleet', 'all', 'master'],
  },
  {
    id: 'docker-images',
    type: 'module',
    category: 'Containers',
    title: 'Docker Images',
    description: 'Manage Docker images',
    path: '/docker/images',
    icon: Box,
    keywords: ['docker', 'images', 'registry'],
  },

  // -------------------------------------------------------------------------
  // NETWORKING MODULES
  // -------------------------------------------------------------------------
  {
    id: 'cisco-switches',
    type: 'module',
    category: 'Networking',
    title: 'Cisco Switches',
    description: 'Manage Cisco switches',
    path: '/cisco-switches',
    icon: Network,
    keywords: ['cisco', 'network', 'switches'],
  },
  {
    id: 'cisco-routers',
    type: 'module',
    category: 'Networking',
    title: 'Cisco Routers',
    description: 'Manage Cisco routers',
    path: '/cisco-routers',
    icon: Router,
    keywords: ['cisco', 'network', 'routers'],
  },
  {
    id: 'vmware-nsx',
    type: 'module',
    category: 'Networking',
    title: 'VMware NSX',
    description: 'Manage VMware NSX network virtualization',
    path: '/vmware-nsx',
    icon: Globe,
    keywords: ['vmware', 'nsx', 'sdn'],
  },

  // -------------------------------------------------------------------------
  // STORAGE MODULES
  // -------------------------------------------------------------------------
  {
    id: 'file-shares',
    type: 'module',
    category: 'Storage',
    title: 'File Shares',
    description: 'Manage network file shares',
    path: '/file-shares',
    icon: FolderOpen,
    keywords: ['smb', 'nfs', 'shares', 'storage'],
  },
  {
    id: 'pure-storage',
    type: 'module',
    category: 'Storage',
    title: 'Pure Storage',
    description: 'Manage Pure Storage arrays',
    path: '/pure-storage',
    icon: HardDriveDownload,
    keywords: ['storage', 'san', 'pure'],
  },

  // -------------------------------------------------------------------------
  // DATABASE MODULES
  // -------------------------------------------------------------------------
  {
    id: 'postgresql',
    type: 'module',
    category: 'Databases',
    title: 'PostgreSQL',
    description: 'Manage PostgreSQL databases',
    path: '/postgresql',
    icon: Database,
    keywords: ['postgres', 'sql', 'database'],
  },
  {
    id: 'mysql',
    type: 'module',
    category: 'Databases',
    title: 'MySQL/MariaDB',
    description: 'Manage MySQL/MariaDB databases',
    path: '/mysql',
    icon: Database,
    keywords: ['mysql', 'mariadb', 'sql'],
  },
  {
    id: 'oracle',
    type: 'module',
    category: 'Databases',
    title: 'Oracle Database',
    description: 'Manage Oracle databases',
    path: '/oracle-db',
    icon: Database,
    keywords: ['oracle', 'sql', 'database'],
  },
  {
    id: 'redis',
    type: 'module',
    category: 'Databases',
    title: 'Redis',
    description: 'Manage Redis cache servers',
    path: '/redis',
    icon: Database,
    keywords: ['redis', 'cache', 'nosql'],
  },

  // -------------------------------------------------------------------------
  // IDENTITY MODULES
  // -------------------------------------------------------------------------
  {
    id: 'ad-domains',
    type: 'module',
    category: 'Identity',
    title: 'AD Domains',
    description: 'Manage Active Directory domains',
    path: '/active-directory/domains',
    icon: Network,
    keywords: ['active directory', 'ad', 'ldap', 'domains'],
  },
  {
    id: 'ad-users',
    type: 'module',
    category: 'Identity',
    title: 'AD Users',
    description: 'Manage Active Directory users',
    path: '/active-directory/users',
    icon: UserCog,
    keywords: ['active directory', 'ad', 'users', 'accounts'],
  },
  {
    id: 'ad-groups',
    type: 'module',
    category: 'Identity',
    title: 'AD Groups',
    description: 'Manage Active Directory groups',
    path: '/active-directory/groups',
    icon: Shield,
    keywords: ['active directory', 'ad', 'groups', 'security'],
  },
  {
    id: 'ad-computers',
    type: 'module',
    category: 'Identity',
    title: 'AD Computers',
    description: 'Manage Active Directory computers',
    path: '/active-directory/computers',
    icon: Monitor,
    keywords: ['active directory', 'ad', 'computers', 'machines'],
  },
  {
    id: 'identity-providers',
    type: 'module',
    category: 'Identity',
    title: 'Identity Providers',
    description: 'Manage SSO and identity providers',
    path: '/identity-providers',
    icon: Key,
    keywords: ['sso', 'saml', 'oauth', 'openid'],
  },

  // -------------------------------------------------------------------------
  // MONITORING MODULES
  // -------------------------------------------------------------------------
  {
    id: 'splunk-instances',
    type: 'module',
    category: 'Monitoring',
    title: 'Splunk Instances',
    description: 'Manage Splunk servers',
    path: '/splunk/instances',
    icon: Database,
    keywords: ['splunk', 'siem', 'logs'],
  },
  {
    id: 'splunk-search',
    type: 'module',
    category: 'Monitoring',
    title: 'Splunk Search',
    description: 'Search Splunk logs',
    path: '/splunk/search',
    icon: Search,
    keywords: ['splunk', 'search', 'query', 'logs'],
  },
  {
    id: 'wazuh',
    type: 'module',
    category: 'Monitoring',
    title: 'Wazuh SIEM',
    description: 'Manage Wazuh security monitoring',
    path: '/wazuh',
    icon: ShieldCheck,
    keywords: ['wazuh', 'siem', 'security', 'ids'],
  },

  // -------------------------------------------------------------------------
  // SECURITY MODULES
  // -------------------------------------------------------------------------
  {
    id: 'tenable',
    type: 'module',
    category: 'Security',
    title: 'Tenable.io',
    description: 'Manage vulnerability scans',
    path: '/tenable',
    icon: Bug,
    keywords: ['tenable', 'vulnerability', 'scanning'],
  },
  {
    id: 'nessus',
    type: 'module',
    category: 'Security',
    title: 'Nessus',
    description: 'Manage Nessus vulnerability scans',
    path: '/nessus',
    icon: Bug,
    keywords: ['nessus', 'vulnerability', 'scanning'],
  },

  // -------------------------------------------------------------------------
  // PATCHING MODULES
  // -------------------------------------------------------------------------
  {
    id: 'wsus',
    type: 'module',
    category: 'Patching',
    title: 'WSUS',
    description: 'Manage Windows updates',
    path: '/wsus',
    icon: RefreshCw,
    keywords: ['wsus', 'windows', 'updates', 'patches'],
  },
  {
    id: 'yum',
    type: 'module',
    category: 'Patching',
    title: 'YUM/DNF Repos',
    description: 'Manage Linux package repositories',
    path: '/yum',
    icon: Package,
    keywords: ['yum', 'dnf', 'rpm', 'packages', 'linux'],
  },

  // -------------------------------------------------------------------------
  // BACKUP MODULES
  // -------------------------------------------------------------------------
  {
    id: 'netbackup',
    type: 'module',
    category: 'Backup',
    title: 'NetBackup',
    description: 'Manage Veritas NetBackup',
    path: '/netbackup',
    icon: Archive,
    keywords: ['backup', 'netbackup', 'veritas'],
  },
  {
    id: 'commvault',
    type: 'module',
    category: 'Backup',
    title: 'Commvault',
    description: 'Manage Commvault backups',
    path: '/commvault',
    icon: Archive,
    keywords: ['backup', 'commvault', 'recovery'],
  },

  // -------------------------------------------------------------------------
  // APPLICATION MODULES
  // -------------------------------------------------------------------------
  {
    id: 'microsoft-appv',
    type: 'module',
    category: 'Applications',
    title: 'Microsoft App-V',
    description: 'Manage virtualized applications',
    path: '/microsoft-appv',
    icon: AppWindow,
    keywords: ['appv', 'app-v', 'virtualization', 'applications'],
  },
  {
    id: 'citrix',
    type: 'module',
    category: 'Applications',
    title: 'Citrix Virtual Apps',
    description: 'Manage Citrix environment',
    path: '/citrix',
    icon: MonitorPlay,
    keywords: ['citrix', 'xenapp', 'virtual apps'],
  },
  {
    id: 'vmware-horizon',
    type: 'module',
    category: 'Applications',
    title: 'VMware Horizon',
    description: 'Manage VDI pools and sessions',
    path: '/vmware-horizon/pools',
    icon: MonitorPlay,
    keywords: ['horizon', 'vdi', 'virtual desktop'],
  },

  // -------------------------------------------------------------------------
  // QUICK ACTIONS
  // -------------------------------------------------------------------------
  {
    id: 'action-create-server',
    type: 'action',
    category: 'Quick Actions',
    title: 'Create Server',
    description: 'Add a new server to EMS',
    icon: Plus,
    keywords: ['add', 'new', 'create', 'server'],
  },
  {
    id: 'action-add-connection',
    type: 'action',
    category: 'Quick Actions',
    title: 'Add Connection',
    description: 'Configure a new integration',
    icon: Link,
    keywords: ['add', 'new', 'connection', 'integration'],
  },
  {
    id: 'action-add-user',
    type: 'action',
    category: 'Quick Actions',
    title: 'Add User',
    description: 'Create a new user account',
    icon: Users,
    keywords: ['add', 'new', 'create', 'user'],
  },
];

// ============================================================================
// Constants
// ============================================================================

const RECENT_SEARCHES_KEY = 'ems-command-palette-recent';
const MAX_RECENT_SEARCHES = 5;

// ============================================================================
// Helper Functions
// ============================================================================

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;

  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <mark
          key={index}
          className="bg-[var(--ems-primary-muted)] text-[var(--ems-primary)] font-medium rounded px-0.5"
        >
          {part}
        </mark>
      );
    }
    return part;
  });
}

function filterItems(items: SearchItem[], query: string): SearchItem[] {
  if (!query.trim()) return [];

  const lowerQuery = query.toLowerCase();

  return items.filter((item) => {
    // Search in title
    if (item.title.toLowerCase().includes(lowerQuery)) return true;

    // Search in description
    if (item.description?.toLowerCase().includes(lowerQuery)) return true;

    // Search in keywords
    if (item.keywords?.some((kw) => kw.toLowerCase().includes(lowerQuery))) return true;

    // Search in category
    if (item.category.toLowerCase().includes(lowerQuery)) return true;

    return false;
  });
}

function groupItems(items: SearchItem[]): SearchGroup[] {
  const grouped: Record<string, SearchItem[]> = {};

  items.forEach((item) => {
    if (!grouped[item.category]) {
      grouped[item.category] = [];
    }
    grouped[item.category].push(item);
  });

  return Object.entries(grouped).map(([category, items]) => ({
    category,
    items,
  }));
}

function saveRecentSearch(item: SearchItem): void {
  try {
    const recent = getRecentSearches();
    const filtered = recent.filter((r) => r.id !== item.id);
    const updated = [item, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('Failed to save recent search:', err);
  }
}

function getRecentSearches(): SearchItem[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    console.error('Failed to load recent searches:', err);
    return [];
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function CommandPalette(): React.ReactElement {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter and group items based on query
  const filteredItems = useMemo(() => filterItems(SEARCH_ITEMS, query), [query]);
  const groupedItems = useMemo(() => groupItems(filteredItems), [filteredItems]);
  const recentSearches = useMemo(() => (isOpen ? getRecentSearches() : []), [isOpen]);

  // Get flat list of all items for keyboard navigation
  const allItems = useMemo(() => {
    if (query.trim()) {
      return filteredItems;
    }
    return recentSearches;
  }, [filteredItems, recentSearches, query]);

  // Open/close handlers
  const openPalette = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  // Handle item selection
  const selectItem = useCallback(
    (item: SearchItem) => {
      if (item.path) {
        navigate(item.path);
        saveRecentSearch(item);
      } else if (item.action) {
        item.action();
      }
      closePalette();
    },
    [navigate, closePalette]
  );

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+K / Ctrl+K to open palette
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }

      // Don't handle other keys if palette is closed
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          closePalette();
          break;

        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(allItems.length, 1));
          break;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + allItems.length) % Math.max(allItems.length, 1));
          break;

        case 'Enter':
          event.preventDefault();
          if (allItems[selectedIndex]) {
            selectItem(allItems[selectedIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, allItems, openPalette, closePalette, selectItem]);

  // Listen for custom event from AdminRail
  useEffect(() => {
    const handleCustomOpen = () => openPalette();
    window.addEventListener('ems:command-palette:open', handleCustomOpen);
    return () => window.removeEventListener('ems:command-palette:open', handleCustomOpen);
  }, [openPalette]);

  // Focus input when palette opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm pt-[20vh] px-4"
      onClick={closePalette}
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
    >
      <div
        className="w-full max-w-2xl bg-[var(--grok-surface-1)] rounded-xl shadow-2xl border border-[var(--grok-border)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--grok-border)]">
          <Search size={20} className="text-[var(--grok-text-muted)] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for pages, modules, settings, actions..."
            className="flex-1 bg-transparent text-[var(--grok-text-primary)] placeholder:text-[var(--grok-text-muted)] outline-none text-base"
            aria-label="Search command palette"
          />
          <button
            onClick={closePalette}
            className="p-1 rounded hover:bg-[var(--grok-hover-bg)] text-[var(--grok-text-muted)] transition-colors"
            aria-label="Close command palette"
          >
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim() ? (
            // Search results
            groupedItems.length > 0 ? (
              <div className="py-2">
                {groupedItems.map((group) => (
                  <div key={group.category} className="mb-4">
                    {/* Category Header */}
                    <div className="px-4 py-2 text-xs font-semibold text-[var(--grok-text-muted)] uppercase tracking-wider">
                      {group.category}
                    </div>

                    {/* Items */}
                    <div>
                      {group.items.map((item) => {
                        const itemIndex = allItems.indexOf(item);
                        const isSelected = itemIndex === selectedIndex;
                        const Icon = item.icon;

                        return (
                          <button
                            key={item.id}
                            onClick={() => selectItem(item)}
                            className={`
                              w-full flex items-center gap-3 px-4 py-3
                              text-left transition-colors
                              ${
                                isSelected
                                  ? 'bg-[var(--grok-active-bg)] text-[var(--grok-text-primary)]'
                                  : 'text-[var(--grok-text-secondary)] hover:bg-[var(--grok-hover-bg)]'
                              }
                            `}
                          >
                            <Icon size={18} className="flex-shrink-0 opacity-60" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">
                                {highlightMatch(item.title, query)}
                              </div>
                              {item.description && (
                                <div className="text-xs text-[var(--grok-text-muted)] mt-0.5 truncate">
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // No results
              <div className="py-12 text-center">
                <Search size={48} className="mx-auto text-[var(--grok-text-muted)] opacity-50 mb-3" />
                <p className="text-[var(--grok-text-muted)]">No results found for &quot;{query}&quot;</p>
                <p className="text-sm text-[var(--grok-text-muted)] mt-1">
                  Try a different search term
                </p>
              </div>
            )
          ) : (
            // Recent searches
            recentSearches.length > 0 ? (
              <div className="py-2">
                <div className="px-4 py-2 text-xs font-semibold text-[var(--grok-text-muted)] uppercase tracking-wider">
                  Recent Searches
                </div>
                <div>
                  {recentSearches.map((item, index) => {
                    const isSelected = index === selectedIndex;
                    // Note: item.icon from localStorage is not a valid component (JSON can't serialize functions)
                    // So we just use Clock icon for all recent searches

                    return (
                      <button
                        key={item.id}
                        onClick={() => selectItem(item)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3
                          text-left transition-colors
                          ${
                            isSelected
                              ? 'bg-[var(--grok-active-bg)] text-[var(--grok-text-primary)]'
                              : 'text-[var(--grok-text-secondary)] hover:bg-[var(--grok-hover-bg)]'
                          }
                        `}
                      >
                        <Clock size={18} className="flex-shrink-0 opacity-60" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{item.title}</div>
                          {item.description && (
                            <div className="text-xs text-[var(--grok-text-muted)] mt-0.5 truncate">
                              {item.description}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Empty state
              <div className="py-12 text-center">
                <Search size={48} className="mx-auto text-[var(--grok-text-muted)] opacity-50 mb-3" />
                <p className="text-[var(--grok-text-muted)]">Start typing to search...</p>
                <p className="text-sm text-[var(--grok-text-muted)] mt-1">
                  Search for pages, modules, settings, and actions
                </p>
              </div>
            )
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-[var(--grok-border)] bg-[var(--grok-surface-2)]">
          <div className="flex items-center gap-4 text-xs text-[var(--grok-text-muted)]">
            <div className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-[var(--grok-surface-1)] border border-[var(--grok-border)] font-mono">
                Enter
              </kbd>
              <span>to select</span>
            </div>
            <div className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-[var(--grok-surface-1)] border border-[var(--grok-border)] font-mono">
                Esc
              </kbd>
              <span>to close</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[var(--grok-text-muted)]">
            <kbd className="px-1.5 py-0.5 rounded bg-[var(--grok-surface-1)] border border-[var(--grok-border)] font-mono">

            </kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-[var(--grok-surface-1)] border border-[var(--grok-border)] font-mono">

            </kbd>
            <span>to navigate</span>
          </div>
        </div>
      </div>
    </div>
  );
}
